import { AxiosResponse } from 'axios'
import { axiosClient } from '@App/api/api-client'
import Pathes from '@App/dicts/pathes'
import { SuggestedMatch } from '@Entities/matching/suggested-match'
import { ResultPage } from '@Entities/result-page'
import { RecordingGroupListFromBebop, RecordingGroup } from '@Entities/matching/recording-group-details'
import { PaginationBody } from '@Entities/pagination'
import { RecordingGroupStatus } from '@Components/MatchComponents/v2/recording-group-status'
import qs from 'qs'
import { MatchSummary } from '@Entities/matching/MatchSummary'
import { MatchSearchFilter, MatchSearchParameters } from '@App/stores/rightsholder/match/matching-store'

export const getUnmatchedRightsholderSearchResults = (
    rightsholderId: number,
    searchParameters: Partial<MatchSearchParameters>,
    filterParameters: Partial<MatchSearchFilter>,
    page: number
): Promise<AxiosResponse<RecordingGroupListFromBebop>> => {
    const queryString = qs.stringify(
        { ...searchParameters, ...filterParameters, page },
        { arrayFormat: 'brackets', skipNulls: true }
    )
    return axiosClient.get<RecordingGroupListFromBebop>(
        Pathes.RightsHolder.unmatchedRightsholderSearch(rightsholderId, queryString)
    )
}

export const getUnmatchedSongwriterSearchResults = (
    songwriterId: string,
    searchParameters: Partial<MatchSearchParameters>,
    filterParameters: Partial<MatchSearchFilter>,
    page: number
): Promise<AxiosResponse<RecordingGroupListFromBebop>> => {
    const queryString = qs.stringify(
        { ...searchParameters, ...filterParameters, page },
        { arrayFormat: 'brackets', skipNulls: true }
    )
    return axiosClient.get<RecordingGroupListFromBebop>(
        Pathes.Songwriter.unmatchedSongwriterSearch(songwriterId, queryString)
    )
}

export const createMatchSuggestion = (
    rightsholderId: number,
    match: { songCode: string; recordingGroupIds: string[]; hfaPublisherNumber: string }
): Promise<AxiosResponse> => axiosClient.post<any>(Pathes.RightsHolder.createMatchSuggestion(rightsholderId), match)

export const getMatchSuggestionHistory = (
    rightsholderId: number,
    status: string[],
    pagination: PaginationBody
): Promise<AxiosResponse<ResultPage<SuggestedMatch>>> =>
    axiosClient.get<ResultPage<SuggestedMatch>>(
        Pathes.RightsHolder.matchSuggestions(rightsholderId, status, pagination)
    )

export const getMatchSuggestion = (
    rightsholderId: number,
    suggestionId: number
): Promise<AxiosResponse<SuggestedMatch>> =>
    axiosClient.get<SuggestedMatch>(Pathes.RightsHolder.singleMatchSuggestion(rightsholderId, suggestionId))

export const getRecordingGroupsDetails = (
    rightsholderId: number,
    suggestionId: number,
    recordingGroupStatus: RecordingGroupStatus[],
    pagination: PaginationBody
): Promise<AxiosResponse<ResultPage<RecordingGroup>>> =>
    axiosClient.get<ResultPage<RecordingGroup>>(
        Pathes.RightsHolder.recordingGroupDetails(rightsholderId, suggestionId, recordingGroupStatus, pagination)
    )

export const getMatchSummary = (rightsholderId: number): Promise<AxiosResponse<MatchSummary>> =>
    axiosClient.get<MatchSummary>(Pathes.RightsHolder.matchSummary(rightsholderId))

export const getSongwriterMatchSummary = (songwriterId: string): Promise<AxiosResponse<MatchSummary>> =>
    axiosClient.get<MatchSummary>(Pathes.Songwriter.matchSummary(songwriterId))

export const getSongwriterSingleMatchSuggestionHistory = (
    songwriterId: string,
    matchingSuggestionId: number
): Promise<AxiosResponse<SuggestedMatch>> =>
    axiosClient.get<SuggestedMatch>(Pathes.Songwriter.matchSuggestion(songwriterId, matchingSuggestionId))

export const getSongwriterMatchSuggestionHistory = (
    songwriterId: string,
    status: string[],
    sort: string,
    pagination: PaginationBody
): Promise<AxiosResponse<ResultPage<SuggestedMatch>>> =>
    axiosClient.get<ResultPage<SuggestedMatch>>(
        Pathes.Songwriter.matchSuggestions(songwriterId, status, sort, pagination)
    )

export const createSongwriterMatchSuggestion = (
    songwriterId: string,
    match: { songCode: string; recordingGroupIds: string[]; userName: string }
): Promise<AxiosResponse> => axiosClient.post<unknown>(Pathes.Songwriter.createMatchSuggestion(songwriterId), match)

export const getSongwriterRecordingGroupsDetails = (
    songwriterId: string,
    suggestionId: number,
    recordingGroupStatus: RecordingGroupStatus[],
    pagination: PaginationBody
): Promise<AxiosResponse<ResultPage<RecordingGroup>>> =>
    axiosClient.get<ResultPage<RecordingGroup>>(
        Pathes.Songwriter.matchRecordingGroupDetails(songwriterId, suggestionId, recordingGroupStatus, pagination)
    )
